import styled from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  muted: "#7d8599",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  inputLabel: "#9ba2b3",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const PageShell = styled.main`
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: ${COLORS.page};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
`;

export const PageFrame = styled.div`
  display: flex;
  width: min(100%, ${PAGE_MAX_WIDTH});
  height: 100%;
  flex-direction: column;
  margin: 0 auto;
  background: ${COLORS.page};
  box-shadow: 0 0 30px rgba(41, 49, 66, 0.04);
`;

export const Header = styled.header`
  display: flex;
  min-height: 76px;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: ${COLORS.white};

  ${MOBILE} {
    min-height: 68px;
    padding: 10px 16px;
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.title};
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 2;
  }

  &:active {
    transform: scale(0.97);
  }

  ${MOBILE} {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
  }
`;

export const HeaderTitle = styled.h1`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 16px;
  }
`;

export const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 24px 24px 16px;

  ${MOBILE} {
    padding: 18px 16px 12px;
  }
`;

export const SectionTitle = styled.h2`
  margin: 0 0 20px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 18px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 18px;
    margin-bottom: 16px;
  }
`;

export const POCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const POCard = styled.article`
  border-radius: 18px;
  padding: 18px 20px;
  background: ${(props) => (props.$disabled ? "#E8EAF5" : COLORS.white)};
  box-shadow: 0 2px 10px rgba(41, 49, 66, 0.07);
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};

  &:active {
    opacity: ${(props) => (props.$disabled ? 1 : 0.96)};
  }

  ${MOBILE} {
    border-radius: 15px;
    padding: 15px 16px;
  }
`;

export const POCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

export const POCheckbox = styled.button`
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  align-items: center;
  justify-content: center;
  border: 2px solid ${(props) => (props.$checked ? COLORS.brand : COLORS.line)};
  border-radius: 7px;
  margin-top: 3px;
  padding: 0;
  background: ${(props) => (props.$checked ? COLORS.brand : COLORS.white)};
  color: ${COLORS.white};
  cursor: pointer;

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 3;
  }

  &:active {
    transform: scale(0.93);
  }
`;

export const POCardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

export const PONumber = styled.h3`
  overflow: hidden;
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 15px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const POSubtitle = styled.div`
  margin-top: 4px;
  color: ${COLORS.muted};
  font-size: 13px;
  line-height: 1.3;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 14px;
  border: 1px solid ${COLORS.line};
  border-radius: 13px;
  padding: 13px 8px;
  background: ${(props) => (props.$disabled ? "#F4F6FB" : "transparent")};

  ${MOBILE} {
    margin-top: 12px;
    padding: 11px 4px;
  }
`;

export const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  text-align: center;
`;

export const MetadataIcon = styled.span`
  display: inline-flex;
  color: ${COLORS.muted};

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 1.9;
  }
`;

export const MetadataValue = styled.span`
  max-width: 100%;
  overflow: hidden;
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
`;

export const InputField = styled.div`
  border: 1px solid ${({ $error }) => ($error ? "#ef4f5f" : COLORS.line)};
  border-radius: 11px;
  padding: 10px 13px;
  background: ${({ $error }) => ($error ? "#fff4f5" : COLORS.white)};
`;

export const InputLabel = styled.div`
  margin-bottom: 5px;
  color: ${COLORS.inputLabel};
  font-size: 12px;
  line-height: 1;
`;

export const InputRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

export const InputControl = styled.input`
  min-width: 0;
  width: 52px;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 17px;
  line-height: 1.25;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: #c4c9d6;
    font-family: ${fontFamilies.regular};
    font-size: 15px;
  }

  ${MOBILE} {
    font-size: 15px;
    width: 44px;
  }
`;

export const InputSuffix = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: ${COLORS.muted};
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Divider = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 4px 0;
`;

export const DividerLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${COLORS.line};
  }
`;

export const DividerLabel = styled.span`
  color: ${COLORS.muted};
  font-family: ${fontFamilies.semiBold};
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const DividerSubtitle = styled.div`
  color: ${COLORS.muted};
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
`;

export const ErrorMessage = styled.p`
  margin: 0 0 10px;
  border-radius: 10px;
  padding: 10px 14px;
  color: #ef4f5f;
  font-size: 13px;
  line-height: 1.45;
  text-align: center;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const CtaBar = styled.div`
  flex: 0 0 auto;
  padding: 16px 24px 24px;
  background: ${COLORS.page};

  ${MOBILE} {
    padding: 12px 16px 20px;
  }
`;

export const ContinueButton = styled.button`
  width: 100%;
  min-height: 56px;
  border: 0;
  border-radius: 16px;
  padding: 0 24px;
  background: ${(props) => (props.disabled ? "#e8e9ed" : COLORS.brand)};
  color: ${(props) => (props.disabled ? "#9ba2b3" : "#ffffff")};
  font-family: ${fontFamilies.semiBold};
  font-size: 18px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};

  &:not(:disabled):active {
    opacity: 0.92;
    transform: scale(0.99);
  }

  ${MOBILE} {
    min-height: 50px;
    font-size: 16px;
  }
`;
